import { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, Button, useTheme } from "@mui/material";
import axios from "axios";
import { tokens } from "../../theme";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import IncomeTable from "../../components/IncomeTable";

dayjs.extend(utc);

const IncomeReport = () => {
  const theme = useTheme();
  // Get today's date in YYYY-MM-DD format
  const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({});
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);

  // Fetch report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/report/income`,
        payload
      );

      setRecords(res.data.records || []);
      setTotals(res.data.totals || {});
    } catch (err) {
      console.error(err);
      setRecords([]);
      setTotals({});
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
  fetchReport();
}, [fetchReport]);

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Income Report
      </Typography>

      {/* Date Filters */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      {/* Summary */}
      <Box display="flex" gap={1.5} mb={3} flexWrap="wrap">
        {[
          { label: "Total Sales", value: totals.totalSales || 0 },
          { label: "Total Paid", value: totals.totalPaid || 0 },
          { label: "Remaining", value: totals.remaining || 0 },
        ].map((item, i) => (
          <Box
            key={i}
            p={1}
            px={2}
            textAlign="center"
            sx={{
              backgroundColor: colors.blueAccent[700],
              borderRadius: 1,
              minWidth: 120,
            }}
          >
            <Typography variant="caption">{item.label}</Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {Number(item.value).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Income Table */}
      <IncomeTable rows={records} summary={totals} themeColors={colors} />
    </Box>
  );
};

export default IncomeReport;
